import {AlertCircle} from 'lucide-react';
import {useId} from 'react';

import {useSkeletonReveal} from '../../hooks';
import {cn} from '../../lib/utils';
import {Field, FieldSkeleton} from '../Field';
import {Input} from '../shadcn/input';

import type {FieldProps} from '../Field';
import type {ComponentProps, ReactNode} from 'react';

export interface FieldInputProps extends ComponentProps<'input'> {
  label?: ReactNode;
  labelSuffix?: ReactNode;
  description?: ReactNode;
  error?: string;
  orientation?: FieldProps['orientation'];
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  badge?: ReactNode;
  /** Corner badge rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Shows a pulsing skeleton in place of the value while it's being loaded/generated. */
  isLoading?: boolean;
  /** Renders a lock glyph next to the label and disables the input — for a permanently non-editable field. */
  isLocked?: boolean;
  /** Tooltip shown on the lock glyph explaining why the field can't be edited. */
  lockedTooltip?: ReactNode;
}

export function FieldInput({
  label,
  labelSuffix,
  description,
  error,
  orientation,
  startAdornment,
  endAdornment,
  badge,
  cornerBadge,
  isLoading,
  isLocked,
  lockedTooltip,
  id,
  className,
  required,
  disabled,
  onFocus,
  ...inputProps
}: FieldInputProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;
  const hasError = !!error;
  const hasLeadingAdornment = hasError || !!startAdornment;
  const hasTrailingAdornment = !!endAdornment;
  const showBadge = !!badge && !endAdornment;
  const {showSkeleton, onFocus: onRevealFocus} = useSkeletonReveal(isLoading);

  return (
    <Field
      label={label}
      labelSuffix={labelSuffix}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      required={required}
      cornerBadge={cornerBadge}
      isLocked={isLocked}
      lockedTooltip={lockedTooltip}
    >
      <div className="relative">
        {hasError ? (
          <AlertCircle
            aria-hidden
            className="text-text-error pointer-events-none absolute inset-s-3 top-1/2 size-4 -translate-y-1/2"
          />
        ) : (
          startAdornment && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-s-3 top-1/2 -translate-y-1/2 text-fg-tertiary"
            >
              {startAdornment}
            </span>
          )
        )}
        {endAdornment && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-e-3 top-1/2 -translate-y-1/2 text-fg-tertiary"
          >
            {endAdornment}
          </span>
        )}
        {showBadge && <span className="absolute inset-e-2 top-1/2 -translate-y-1/2">{badge}</span>}
        {showSkeleton && (
          <FieldSkeleton
            className={cn(
              'top-1/2 -translate-y-1/2',
              hasLeadingAdornment ? 'inset-s-9' : 'inset-s-3'
            )}
          />
        )}
        <Input
          id={resolvedId}
          aria-invalid={hasError || undefined}
          required={required}
          disabled={disabled || isLocked}
          onFocus={e => {
            onRevealFocus();
            onFocus?.(e);
          }}
          className={cn(
            hasLeadingAdornment && 'ps-9',
            hasTrailingAdornment && 'pe-9',
            showBadge && 'pe-14',
            showSkeleton && 'placeholder:opacity-0',
            className
          )}
          {...inputProps}
        />
      </div>
    </Field>
  );
}
